/**
 * 4D Time Cube — stacks video frames into an (x, y, t) volume, peels the layers
 * back as it plays, and stains each layer by how it differs from the ones behind it.
 */

import './ui/styles.css';

import { nextAnimationFrame, yieldToEventLoop } from './core/async';
import { OrbitCamera } from './core/camera';
import { ScreenQuad, ShaderError, createGLContext } from './core/gl';
import { CancelledError, Ingest } from './core/ingest';
import { clamp } from './core/mat4';
import { PRESETS, presetByName } from './core/presets';
import { Recorder, downloadBlob, timestampSlug } from './core/recorder';
import { VolumeRenderer } from './core/renderer';
import {
  COLORMAPS,
  COMPARE_TARGETS,
  CUSTOM_RULE_REFERENCE,
  DEFAULT_CUSTOM_RULE,
  RULES,
  ruleByKey,
  type RuleKey,
} from './core/rules';
import { StainPass } from './core/stain';
import { Store, type Settings } from './core/state';
import { SYNTHETIC_PATTERNS, SyntheticSource, type SyntheticPattern } from './core/synthetic';
import { Volume } from './core/volume';
import { Panel } from './ui/panel';

type Mode = 'idle' | 'volume' | 'live';

const COLOR_MODES = [
  { value: 0, label: 'video only' },
  { value: 1, label: 'stain only' },
  { value: 2, label: 'stain over video' },
  { value: 3, label: 'stain added' },
  { value: 4, label: 'stain multiplied' },
];

const DENSITY_MODES = [
  { value: 0, label: 'solid block' },
  { value: 1, label: 'stain (difference)' },
  { value: 2, label: 'inverse stain (similarity)' },
  { value: 3, label: 'image luminance' },
  { value: 4, label: 'mostly solid + stain' },
];

const TONEMAPS = [
  { value: 0, label: 'none' },
  { value: 1, label: 'reinhard' },
  { value: 2, label: 'aces' },
  { value: 3, label: 'filmic' },
];

const LOOP_MODES = [
  { value: 'loop', label: 'loop' },
  { value: 'once', label: 'play once' },
  { value: 'pingpong', label: 'ping-pong' },
];

const PEEL_MODES = [
  { value: 'consume', label: 'peel away (solid \u2192 last frame)' },
  { value: 'accumulate', label: 'build up (first frame \u2192 solid)' },
];

const DIRECTIONS = [
  { value: 1, label: 'forward through the clip' },
  { value: -1, label: 'backward through the clip' },
];

const ANCHORS = [
  { value: 'front', label: 'front face fixed' },
  { value: 'center', label: 'centered' },
  { value: 'back', label: 'back face fixed' },
];

function requireElement<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id} in the document`);
  return node as T;
}

class App {
  private readonly canvas = requireElement<HTMLCanvasElement>('view');
  private readonly panelRoot = requireElement<HTMLElement>('panel');
  private readonly emptyOverlay = requireElement<HTMLElement>('empty');
  private readonly dropOverlay = requireElement<HTMLElement>('drop-overlay');
  private readonly progressOverlay = requireElement<HTMLElement>('progress');
  private readonly progressFill = requireElement<HTMLElement>('progress-fill');
  private readonly progressLabel = requireElement<HTMLElement>('progress-label');
  private readonly progressCancel = requireElement<HTMLButtonElement>('progress-cancel');
  private readonly toasts = requireElement<HTMLElement>('toasts');
  private readonly statsEl = requireElement<HTMLElement>('hud-stats');
  private readonly fileInput = requireElement<HTMLInputElement>('file-input');
  private readonly timelineTrack = requireElement<HTMLElement>('timeline-track');
  private readonly timelineFill = requireElement<HTMLElement>('timeline-fill');
  private readonly timelineHead = requireElement<HTMLElement>('timeline-head');
  private readonly timelineLayer = requireElement<HTMLElement>('timeline-layer');
  private readonly recDot: HTMLElement;

  private readonly store = new Store();
  private readonly ctx = createGLContext(this.canvas);
  private readonly quad = new ScreenQuad(this.ctx.gl);
  private readonly volume = new Volume(this.ctx);
  private readonly stain = new StainPass(this.ctx, this.quad);
  private readonly renderer = new VolumeRenderer(this.ctx, this.quad);
  private readonly camera = new OrbitCamera();
  private readonly ingest = new Ingest();
  private readonly recorder = new Recorder(this.canvas);
  private readonly panel: Panel;
  private readonly synthetic = new SyntheticSource();

  private mode: Mode = 'idle';
  private needsBake = false;
  private ingesting = false;
  private exporting = false;
  private reslicePending = 0;
  private loadQueue: Promise<void> = Promise.resolve();
  private lastTime = 0;
  private lastIngestDraw = 0;
  private fpsAccumulator = 0;
  private fpsFrames = 0;
  private fps = 0;
  private lastStats = { samples: 0, steps: 0 };
  private syntheticPattern: SyntheticPattern = 'crossing';
  private syntheticFrames = 192;
  private presetName = PRESETS[0].name;

  private headSlider: { setRange(min: number, max: number, step?: number): void; refresh(): void } | null = null;
  private slabSlider: { setRange(min: number, max: number, step?: number): void; refresh(): void } | null = null;
  private traceSlider: { setRange(min: number, max: number, step?: number): void; refresh(): void } | null = null;
  private sourceReadout: { set(html: string): void } | null = null;
  private exportReadout: { set(html: string): void } | null = null;
  private ruleNote: { set(html: string): void } | null = null;
  private customCode: { setError(message: string | null): void; setVisible(visible: boolean): void } | null = null;
  private recordButton: HTMLElement | null = null;

  constructor() {
    this.recDot = document.createElement('div');
    this.recDot.className = 'rec-dot';
    this.recDot.innerHTML = '<i></i><span>recording</span>';
    requireElement<HTMLElement>('app').appendChild(this.recDot);

    this.panel = new Panel(this.panelRoot, this.store);
    this.stain.compile(this.store.settings.rule.customSrc);

    this.camera.onChange = () => this.renderer.invalidate();
    this.camera.attach(this.canvas);

    this.buildPanel();
    this.bindStore();
    this.bindDom();
    this.bindKeyboard();
    this.observeResize();

    this.emptyOverlay.classList.add('show');
    this.exposeConsoleApi();
    this.loop(performance.now());
  }

  /** `timeCube` in the devtools console, for scripting looks and driving the peel. */
  private exposeConsoleApi(): void {
    const api = {
      store: this.store,
      volume: this.volume,
      camera: this.camera,
      renderer: this.renderer,
      set: (path: string, value: unknown) => this.store.set(path, value),
      get: (path: string) => this.store.get(path),
      preset: (name: string) => this.applyPreset(name),
      synthetic: (pattern: SyntheticPattern) => {
        this.syntheticPattern = pattern;
        return this.loadSynthetic();
      },
      customRule: (source: string) => this.applyCustomRule(source),
      loadUrl: (url: string) => this.loadFromUrl(url),
      rebake: () => {
        this.needsBake = true;
      },
    };
    (window as unknown as Record<string, unknown>).timeCube = api;
  }

  // ---------------------------------------------------------------- lifecycle

  private observeResize(): void {
    const apply = () => {
      const rect = this.canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.renderer.setSize(rect.width, rect.height, dpr, this.store.settings.look.resolutionScale);
    };
    new ResizeObserver(apply).observe(this.canvas);
    window.addEventListener('resize', apply);
    apply();

    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.toast('The GPU context was lost. Reload the page to continue.', 'err');
    });
  }

  private loop = (now: number): void => {
    requestAnimationFrame(this.loop);
    const dt = this.lastTime ? Math.min((now - this.lastTime) / 1000, 0.25) : 0;
    this.lastTime = now;

    this.fpsAccumulator += dt;
    this.fpsFrames++;
    if (this.fpsAccumulator >= 0.5) {
      this.fps = this.fpsFrames / this.fpsAccumulator;
      this.fpsAccumulator = 0;
      this.fpsFrames = 0;
    }

    if (this.exporting) return;

    this.advancePlayhead(dt);
    if (this.store.settings.camera.autoRotate) {
      this.camera.spin(this.store.settings.camera.autoRotateSpeed * dt);
    }

    // While ingesting, the ray march competes with video decoding for the GPU, so
    // preview at a low rate and give the decoder the rest.
    if (this.ingesting && now - this.lastIngestDraw < 100) {
      return;
    }
    this.lastIngestDraw = now;

    this.drawFrame();
    this.updateHud();
  };

  /** Bakes anything outstanding and draws one accumulation sample. */
  private drawFrame(): void {
    if (this.needsBake && !this.ingesting) {
      this.needsBake = false;
      try {
        this.stain.bakeAll(this.volume, this.store.settings.rule);
      } catch (err) {
        this.toast(err instanceof Error ? err.message : String(err), 'err');
      }
      this.renderer.invalidate();
    }

    this.camera.fov = this.store.settings.camera.fov;
    const stats = this.renderer.render(this.volume, this.store.settings, this.camera);
    this.lastStats = { samples: stats.samples, steps: stats.steps };
  }

  private advancePlayhead(dt: number): void {
    const { peel } = this.store.settings;
    const maxLayer = Math.max(0, this.volume.count - 1);

    if (this.mode === 'live') {
      this.setHead(maxLayer);
      return;
    }
    if (this.ingesting || !peel.playing || maxLayer <= 0 || dt <= 0) return;

    let head = peel.head + peel.direction * peel.speed * dt;

    if (peel.loop === 'loop') {
      if (head > maxLayer) head -= maxLayer;
      if (head < 0) head += maxLayer;
    } else if (peel.loop === 'pingpong') {
      if (head > maxLayer) {
        head = maxLayer - (head - maxLayer);
        this.store.set('peel.direction', -1);
      } else if (head < 0) {
        head = -head;
        this.store.set('peel.direction', 1);
      }
    } else {
      if (head > maxLayer || head < 0) {
        head = clamp(head, 0, maxLayer);
        this.store.set('peel.playing', false);
      }
    }

    this.setHead(clamp(head, 0, maxLayer));
  }

  /** Updates the playhead without triggering a full panel refresh every frame. */
  private setHead(head: number): void {
    const current = this.store.settings.peel.head;
    if (current === head) return;
    this.store.set('peel.head', head, true);
    this.renderer.invalidate();
    this.headSlider?.refresh();
    this.updateTimeline();
  }

  // ------------------------------------------------------------------- panel

  private buildPanel(): void {
    this.buildSourceFolder();
    this.buildRuleFolder();
    this.buildPeelFolder();
    this.buildLookFolder();
    this.buildImageFolder();
    this.buildCameraFolder();
    this.buildPresetFolder();
    this.buildExportFolder();
  }

  private buildSourceFolder(): void {
    const folder = this.panel.folder('Source');

    folder.buttons([
      { label: 'Open video\u2026', onClick: () => this.fileInput.click(), primary: true },
      { label: 'Webcam', onClick: () => void this.startWebcam() },
    ]);
    folder.buttons([
      { label: 'Generate synthetic clip', onClick: () => void this.loadSynthetic() },
    ]);

    const patternSelect = document.createElement('select');
    for (const pattern of SYNTHETIC_PATTERNS) {
      const option = document.createElement('option');
      option.value = pattern.value;
      option.textContent = pattern.label;
      patternSelect.appendChild(option);
    }
    patternSelect.value = this.syntheticPattern;
    patternSelect.addEventListener('change', () => {
      this.syntheticPattern = patternSelect.value as SyntheticPattern;
    });
    const patternRow = document.createElement('div');
    patternRow.className = 'row';
    const patternLabel = document.createElement('label');
    patternLabel.textContent = 'pattern';
    const patternCtrl = document.createElement('div');
    patternCtrl.className = 'ctrl';
    patternCtrl.appendChild(patternSelect);
    patternRow.append(patternLabel, patternCtrl);
    folder.body.appendChild(patternRow);

    folder.slider('frame width (x)', 'source.width', { min: 32, max: 512, step: 16 });
    folder.slider('frame height (y)', 'source.height', { min: 32, max: 512, step: 8 });
    folder.slider('layers (t)', 'source.layers', { min: 8, max: 512, step: 8 });
    folder.select('capture', 'source.captureMode', [
      { value: 'auto', label: 'auto (exact when possible)' },
      { value: 'even', label: 'seek evenly across clip' },
      { value: 'sequential', label: 'play through (may drop frames)' },
    ]);
    folder.slider('min gap (s)', 'source.captureInterval', { min: 0, max: 1, step: 0.01 });
    folder.toggle('skip still frames', 'source.skipDuplicates');
    folder.button('Re-slice source', () => void this.reslice());

    this.sourceReadout = folder.readout();
    this.updateSourceReadout();
  }

  private buildRuleFolder(): void {
    const folder = this.panel.folder('Stain rule');

    folder.select(
      'rule',
      'rule.key',
      RULES.map((rule) => ({ value: rule.key, label: rule.label })),
    );
    this.ruleNote = folder.note('');

    folder.select('compare against', 'rule.compare', COMPARE_TARGETS);
    folder.slider('stride (layers)', 'rule.stride', { min: 1, max: 48, step: 1 });
    folder.slider('pre-blur', 'rule.blur', { min: 0, max: 3, step: 1 });
    folder.slider('gain', 'rule.gain', { min: 0, max: 20, step: 0.05 });
    folder.slider('bias', 'rule.bias', { min: -0.5, max: 0.5, step: 0.005 });
    folder.slider('threshold', 'rule.threshold', { min: 0, max: 1, step: 0.005 });
    folder.slider('softness', 'rule.softness', { min: 0.001, max: 0.3, step: 0.001 });
    folder.slider('trail decay', 'rule.decay', { min: 0.5, max: 0.999, step: 0.001 });
    folder.slider('mean rate', 'rule.meanRate', { min: 0.002, max: 0.5, step: 0.002 });
    folder.slider('curve (gamma)', 'rule.gamma', { min: 0.2, max: 4, step: 0.02 });
    folder.select('colormap', 'rule.colormap', COLORMAPS);
    folder.toggle('tint with source', 'rule.sourceColor');
    folder.slider('param a', 'rule.paramA', { min: 0, max: 1, step: 0.005 });
    folder.slider('param b', 'rule.paramB', { min: 0, max: 1, step: 0.005 });
    folder.slider('param c', 'rule.paramC', { min: 0, max: 1, step: 0.005 });

    this.customCode = folder.code('custom glsl', 'rule.customSrc', (source) => this.applyCustomRule(source));
    folder.note(`<b>available in a custom rule</b><br>${CUSTOM_RULE_REFERENCE.replace(/\n/g, '<br>')}`);
    folder.button('Restore example rule', () => {
      this.store.set('rule.customSrc', DEFAULT_CUSTOM_RULE);
      this.applyCustomRule(DEFAULT_CUSTOM_RULE);
    });

    this.syncRuleUi();
  }

  private buildPeelFolder(): void {
    const folder = this.panel.folder('Peel / time');

    folder.toggle('playing', 'peel.playing');
    this.headSlider = folder.slider('playhead (t)', 'peel.head', { min: 0, max: 1, step: 0.01 });
    folder.slider('layers / second', 'peel.speed', { min: 0.25, max: 120, step: 0.25 });
    folder.select('mode', 'peel.mode', PEEL_MODES);
    folder.select('direction', 'peel.direction', DIRECTIONS);
    folder.select('at the end', 'peel.loop', LOOP_MODES);
    this.slabSlider = folder.slider('block depth', 'peel.slabDepth', { min: 1, max: 512, step: 1 });
    this.traceSlider = folder.slider('trace depth', 'peel.traceDepth', { min: 0, max: 512, step: 1 });
    folder.slider('trace opacity', 'peel.traceAlpha', { min: 0, max: 1, step: 0.005 });
    folder.slider('trace fade', 'peel.traceFade', { min: 0, max: 0.3, step: 0.001 });
    folder.slider('trace is stain', 'peel.traceStain', { min: 0, max: 1, step: 0.01 });
    folder.toggle('shrink box to block', 'peel.fitBox');
    folder.select('anchor', 'peel.anchor', ANCHORS);
    folder.note(
      'The playhead is the exposed face. <b>Peeling away</b> starts from a solid cube and ' +
        'strips each layer off as it plays, down to the final frame; the layers already gone ' +
        'linger as the <b>trace</b>. Turn <b>trace is stain</b> down to leave a ghost of the ' +
        'footage instead of only what changed.',
    );
  }

  private buildLookFolder(): void {
    const folder = this.panel.folder('Look');

    folder.select('color', 'look.colorMode', COLOR_MODES);
    folder.select('density from', 'look.densityMode', DENSITY_MODES);
    folder.slider('density', 'look.density', { min: 0, max: 24, step: 0.05 });
    folder.slider('stain mix', 'look.stainMix', { min: 0, max: 1, step: 0.005 });
    folder.slider('stain glow', 'look.stainGlow', { min: 0, max: 1.5, step: 0.005 });
    folder.slider('stain cutoff', 'look.cutoff', { min: 0, max: 0.95, step: 0.005 });
    folder.slider('exposed face', 'look.frontOpacity', { min: 0, max: 1, step: 0.005 });
    folder.slider('depth shading', 'look.depthShade', { min: 0, max: 2.5, step: 0.01 });
    folder.slider('t depth scale', 'look.depthScale', { min: 0.1, max: 3, step: 0.01 });
    folder.toggle('blend between layers', 'look.trilinear');
    folder.toggle('box wireframe', 'look.wire');
    folder.slider('wire opacity', 'look.wireAlpha', { min: 0, max: 1, step: 0.005 });
    folder.color('wire color', 'look.wireColor');
  }

  private buildImageFolder(): void {
    const folder = this.panel.folder('Image', false);

    folder.slider('exposure', 'look.exposure', { min: 0, max: 4, step: 0.01 });
    folder.select('tonemap', 'look.tonemap', TONEMAPS);
    folder.slider('gamma', 'look.gamma', { min: 1, max: 3, step: 0.01 });
    folder.color('background', 'look.background');
    folder.slider('bg falloff', 'look.backgroundFalloff', { min: 0, max: 10, step: 0.05 });
    folder.slider('vignette', 'look.vignette', { min: 0, max: 1, step: 0.005 });
    folder.toggle('refine when still', 'look.accumulate');
    folder.slider('max samples', 'look.maxSamples', { min: 1, max: 256, step: 1 });
    folder.slider('ray jitter', 'look.jitter', { min: 0, max: 2, step: 0.01 });
    folder.toggle('auto step count', 'look.autoSteps');
    folder.slider('steps', 'look.steps', { min: 24, max: 1024, step: 8 });
    folder.slider('render scale', 'look.resolutionScale', { min: 0.25, max: 2, step: 0.05 });
  }

  private buildCameraFolder(): void {
    const folder = this.panel.folder('Camera', false);

    folder.slider('field of view', 'camera.fov', { min: 10, max: 90, step: 0.5 });
    folder.toggle('auto rotate', 'camera.autoRotate');
    folder.slider('rotate speed', 'camera.autoRotateSpeed', { min: -1, max: 1, step: 0.005 });
    folder.buttons([
      { label: 'x / y', onClick: () => this.camera.preset(0) },
      { label: 'x / t', onClick: () => this.camera.preset(1) },
      { label: 'y / t', onClick: () => this.camera.preset(2) },
      { label: '3\u20444', onClick: () => this.camera.preset(3) },
    ]);
    folder.buttons([
      { label: 'Reset target', onClick: () => this.camera.reset() },
      { label: 'Frame block', onClick: () => this.frameCamera() },
    ]);
    folder.note('Drag to orbit, shift-drag to pan, scroll to zoom.');
  }

  private buildPresetFolder(): void {
    const folder = this.panel.folder('Presets');

    const select = document.createElement('select');
    for (const preset of PRESETS) {
      const option = document.createElement('option');
      option.value = preset.name;
      option.textContent = preset.name;
      select.appendChild(option);
    }
    const row = document.createElement('div');
    row.className = 'row';
    const label = document.createElement('label');
    label.textContent = 'look';
    const ctrl = document.createElement('div');
    ctrl.className = 'ctrl';
    ctrl.appendChild(select);
    row.append(label, ctrl);
    folder.body.appendChild(row);

    const note = folder.note(PRESETS[0].blurb);
    select.value = this.presetName;
    select.addEventListener('change', () => {
      this.presetName = select.value;
      note.set(presetByName(this.presetName)?.blurb ?? '');
    });

    folder.buttons([
      { label: 'Apply', onClick: () => this.applyPreset(this.presetName), primary: true },
      {
        label: 'Next',
        onClick: () => {
          const index = PRESETS.findIndex((p) => p.name === this.presetName);
          const next = PRESETS[(index + 1) % PRESETS.length];
          this.presetName = next.name;
          select.value = next.name;
          note.set(next.blurb);
          this.applyPreset(next.name);
        },
      },
    ]);

    folder.buttons([
      { label: 'Save settings', onClick: () => this.saveSettings() },
      { label: 'Load settings', onClick: () => this.loadSettings() },
    ]);
  }

  private buildExportFolder(): void {
    const folder = this.panel.folder('Export', false);

    folder.buttons([{ label: 'Save PNG', onClick: () => void this.savePng(), primary: true }]);
    const [recordButton] = folder.buttons([
      { label: 'Record canvas', onClick: () => void this.toggleRecording() },
    ]);
    this.recordButton = recordButton;
    folder.buttons([{ label: 'Export one full peel', onClick: () => void this.exportPeel() }]);
    this.exportReadout = folder.readout();
    this.exportReadout.set('Recording captures exactly what is on screen. A peel export renders one video frame per layer.');
  }

  // ------------------------------------------------------------------- store

  private bindStore(): void {
    this.store.onChange((event) => {
      switch (event.invalidation) {
        case 'volume':
          this.scheduleReslice();
          this.updateSourceReadout();
          break;
        case 'shader':
          break;
        case 'stain':
          this.needsBake = true;
          this.renderer.invalidate();
          break;
        default:
          this.renderer.invalidate();
          break;
      }

      if (event.path === 'rule.key') {
        this.applyRuleDefaults(event.value as RuleKey);
        this.syncRuleUi();
      }
      if (event.path === 'look.resolutionScale') {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.renderer.setSize(rect.width, rect.height, dpr, this.store.settings.look.resolutionScale);
      }
      if (event.path === 'source.width') {
        this.syncHeightToAspect();
      }
    });
  }

  private applyRuleDefaults(key: RuleKey): void {
    const suggests = ruleByKey(key).suggests;
    if (!suggests) return;
    for (const [name, value] of Object.entries(suggests)) {
      this.store.set(`rule.${name}`, value);
    }
  }

  private syncRuleUi(): void {
    const rule = ruleByKey(this.store.settings.rule.key);
    this.ruleNote?.set(rule.blurb);
    this.customCode?.setVisible(rule.key === 'custom');
  }

  private applyCustomRule(source: string): void {
    this.store.set('rule.customSrc', source);
    try {
      this.stain.compile(source);
      this.customCode?.setError(null);
      this.needsBake = true;
      this.renderer.invalidate();
      this.toast('Custom rule compiled.', 'ok');
    } catch (err) {
      const message = err instanceof ShaderError ? err.message : err instanceof Error ? err.message : String(err);
      this.customCode?.setError(message);
      this.toast('The custom rule did not compile. See the panel for details.', 'err');
    }
  }

  private applyPreset(name: string): void {
    const preset = presetByName(name);
    if (!preset) return;

    // Selecting a rule pulls in that rule's suggested parameters, so it has to be
    // applied before the preset's own overrides rather than alongside them.
    const { 'rule.key': ruleKey, ...rest } = preset.patch;
    if (ruleKey !== undefined) this.store.set('rule.key', ruleKey);
    this.store.patch(rest);
    this.clampPeelRanges();
    this.needsBake = true;
    this.renderer.invalidate();
    this.toast(`Preset: ${preset.name}`, 'ok');
  }

  private saveSettings(): void {
    const data = JSON.stringify(this.store.toJSON(), null, 2);
    downloadBlob(new Blob([data], { type: 'application/json' }), `timecube-${timestampSlug()}.json`);
  }

  private loadSettings(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const data = JSON.parse(await file.text()) as Partial<Settings>;
        // Keep the current clip: skip anything that would force a re-slice.
        this.store.load(data, ['source', 'peel.head']);
        this.applyCustomRule(this.store.settings.rule.customSrc);
        this.clampPeelRanges();
        this.panel.refresh();
        this.syncRuleUi();
        this.toast(`Loaded settings from ${file.name}`, 'ok');
      } catch (err) {
        this.toast(`Could not read those settings: ${err instanceof Error ? err.message : String(err)}`, 'err');
      }
    });
    input.click();
  }

  // ------------------------------------------------------------------ ingest

  /**
   * Loads run one at a time. A new request cancels the capture in flight, then waits
   * for it to unwind so two ingests never write into the volume at once.
   */
  private enqueueLoad(task: () => Promise<void>): Promise<void> {
    this.ingest.cancel();
    const run = this.loadQueue.catch(() => undefined).then(task);
    this.loadQueue = run.catch(() => undefined);
    return run;
  }

  private loadFile(file: File): Promise<void> {
    return this.enqueueLoad(() => this.loadFileTask(file));
  }

  private loadFromUrl(url: string): Promise<void> {
    return this.enqueueLoad(() => this.loadFromUrlTask(url));
  }

  private loadSynthetic(): Promise<void> {
    return this.enqueueLoad(() => this.loadSyntheticTask());
  }

  private startWebcam(): Promise<void> {
    return this.enqueueLoad(() => this.startWebcamTask());
  }

  private async loadFileTask(file: File): Promise<void> {
    try {
      const info = await this.ingest.loadFile(file);
      this.syncHeightToAspect(info.width, info.height);
      this.mode = 'volume';
      await this.capture();
      this.toast(`${info.name} \u2014 ${this.volume.count} layers`, 'ok');
    } catch (err) {
      if (err instanceof CancelledError) return;
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    }
  }

  /** Loads a clip by URL. Cross-origin sources must send permissive CORS headers. */
  private async loadFromUrlTask(url: string): Promise<void> {
    try {
      const info = await this.ingest.loadUrl(url);
      this.syncHeightToAspect(info.width, info.height);
      this.mode = 'volume';
      await this.capture();
      this.toast(`${info.name} \u2014 ${this.volume.count} layers`, 'ok');
    } catch (err) {
      if (err instanceof CancelledError) return;
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    }
  }

  private async loadSyntheticTask(): Promise<void> {
    this.ingest.stopLive();
    this.mode = 'volume';
    this.ingesting = true;
    this.showProgress('generating frames');

    try {
      const { source } = this.store.settings;
      this.syncHeightToAspect(this.synthetic.width, this.synthetic.height);
      this.volume.allocate({ width: source.width, height: source.height, layers: source.layers });
      this.syntheticFrames = source.layers;
      this.synthetic.reset(this.syntheticPattern);

      for (let i = 0; i < this.syntheticFrames; i++) {
        const canvas = this.synthetic.render(i, this.syntheticFrames);
        const layer = this.volume.push(canvas, i / 30);
        this.onLayerIngested(layer);
        if (i % 8 === 0 || i === this.syntheticFrames - 1) {
          this.setProgress(i + 1, this.syntheticFrames, 'generating frames');
          await yieldToEventLoop();
        }
      }

      this.ingest.info = {
        kind: 'synthetic',
        name: this.syntheticPattern,
        width: this.synthetic.width,
        height: this.synthetic.height,
        duration: this.syntheticFrames / 30,
        frameRate: 30,
      };
      this.finishIngest();
      this.toast(`Synthetic "${this.syntheticPattern}" \u2014 ${this.volume.count} layers`, 'ok');
    } catch (err) {
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    } finally {
      this.ingesting = false;
      this.hideProgress();
    }
  }

  private async startWebcamTask(): Promise<void> {
    try {
      const info = await this.ingest.startWebcam();
      this.syncHeightToAspect(info.width, info.height);
      const { source } = this.store.settings;
      this.volume.allocate({ width: source.width, height: source.height, layers: source.layers });
      this.stain.invalidate();
      this.mode = 'live';
      this.store.set('peel.playing', false);
      this.ingest.startLive(this.volume, this.store.settings.source, (layer) => this.onLiveLayer(layer));
      this.emptyOverlay.classList.remove('show');
      this.frameCamera();
      this.updateSourceReadout();
      this.toast(`Live: ${info.name}. The cube holds the last ${source.layers} frames.`, 'ok');
    } catch (err) {
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    }
  }

  private async capture(): Promise<void> {
    const { source } = this.store.settings;
    this.ingesting = true;
    this.showProgress('slicing clip into layers');
    try {
      this.volume.allocate({ width: source.width, height: source.height, layers: source.layers });
      this.stain.invalidate();
      await this.ingest.captureAll(this.volume, source, {
        onProgress: (p) => this.setProgress(p.done, p.total, p.label),
        onLayer: (layer) => this.onLayerIngested(layer),
      });
      this.finishIngest();
    } finally {
      this.ingesting = false;
      this.hideProgress();
    }
  }

  /** Bakes each layer as it arrives so the block visibly fills up during ingest. */
  private onLayerIngested(layer: number): void {
    try {
      this.stain.bakeLayer(this.volume, this.store.settings.rule, layer, layer === 0);
    } catch {
      // A bad custom rule should not abort ingest; the error is already surfaced.
    }
    this.store.set('peel.head', layer, true);
    this.renderer.invalidate();
    this.emptyOverlay.classList.remove('show');
  }

  private onLiveLayer(layer: number): void {
    try {
      this.stain.bakeLayer(this.volume, this.store.settings.rule, layer, this.volume.count === 1);
    } catch {
      // Same as above: keep the live feed running.
    }
    this.renderer.invalidate();
  }

  private finishIngest(): void {
    const info = this.ingest.info;
    this.clampPeelRanges();
    if (info && info.frameRate > 0.5 && Number.isFinite(info.frameRate)) {
      this.store.set('peel.speed', Math.min(120, Math.round(info.frameRate * 4) / 4));
    }
    this.store.set('peel.head', 0);
    this.store.set('peel.playing', true);
    this.needsBake = true;
    this.frameCamera();
    this.updateSourceReadout();
    this.emptyOverlay.classList.remove('show');
  }

  private scheduleReslice(): void {
    if (this.reslicePending) window.clearTimeout(this.reslicePending);
    this.reslicePending = window.setTimeout(() => {
      this.reslicePending = 0;
      void this.reslice();
    }, 700);
  }

  private async reslice(): Promise<void> {
    if (this.mode === 'idle') return;

    if (this.mode === 'live') {
      return this.enqueueLoad(async () => {
        const { source } = this.store.settings;
        this.ingest.stopLive();
        this.volume.allocate({ width: source.width, height: source.height, layers: source.layers });
        this.stain.invalidate();
        this.ingest.startLive(this.volume, source, (layer) => this.onLiveLayer(layer));
      });
    }

    if (this.ingest.info?.kind === 'synthetic') {
      return this.loadSynthetic();
    }
    if (!this.ingest.element) return;

    return this.enqueueLoad(async () => {
      try {
        await this.capture();
      } catch (err) {
        if (err instanceof CancelledError) return;
        this.toast(err instanceof Error ? err.message : String(err), 'err');
      }
    });
  }

  private clampPeelRanges(): void {
    const maxLayer = Math.max(1, this.volume.count - 1);
    const layers = Math.max(1, this.volume.layers);
    this.headSlider?.setRange(0, maxLayer, 0.01);
    this.slabSlider?.setRange(1, layers, 1);
    this.traceSlider?.setRange(0, layers, 1);
    // Presets ask for more depth than any volume has, meaning "all of it".
    const { peel } = this.store.settings;
    if (peel.slabDepth > layers) this.store.set('peel.slabDepth', layers);
    if (peel.traceDepth > layers) this.store.set('peel.traceDepth', layers);
    this.store.set('peel.head', clamp(peel.head, 0, maxLayer));
  }

  private syncHeightToAspect(sourceWidth?: number, sourceHeight?: number): void {
    const info = this.ingest.info;
    const w = sourceWidth ?? info?.width ?? 16;
    const h = sourceHeight ?? info?.height ?? 9;
    if (!w || !h) return;
    const target = clamp(Math.round((this.store.settings.source.width * h) / w / 8) * 8, 32, 512);
    this.store.set('source.height', target, true);
  }

  private frameCamera(): void {
    const look = this.store.settings.look;
    const hy = 0.5 * (this.volume.height / Math.max(1, this.volume.width));
    const radius = Math.hypot(0.5, hy, 0.5 * look.depthScale);
    this.camera.frame(radius);
  }

  // -------------------------------------------------------------------- export

  private async savePng(): Promise<void> {
    this.drawFrame();
    try {
      await this.recorder.savePng(`timecube-${timestampSlug()}.png`);
      this.toast('Saved a PNG.', 'ok');
    } catch (err) {
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    }
  }

  private async toggleRecording(): Promise<void> {
    if (this.recorder.isRecording) {
      const blob = await this.recorder.stop();
      this.recDot.classList.remove('show');
      this.recordButton?.classList.remove('active');
      if (blob) {
        const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
        downloadBlob(blob, `timecube-${timestampSlug()}.${extension}`);
        this.toast(`Saved ${(blob.size / 1e6).toFixed(1)} MB of video.`, 'ok');
      }
      return;
    }

    try {
      this.recorder.start({ fps: 60 });
      this.recDot.classList.add('show');
      this.recordButton?.classList.add('active');
      this.toast('Recording the canvas. Press R again to stop.', 'ok');
    } catch (err) {
      this.toast(err instanceof Error ? err.message : String(err), 'err');
    }
  }

  /** Renders one video frame per layer, with full accumulation on each. */
  private async exportPeel(): Promise<void> {
    if (this.volume.count < 2) {
      this.toast('Load a clip first.', 'err');
      return;
    }
    if (this.recorder.isRecording) {
      this.toast('Stop the current recording first.', 'err');
      return;
    }

    const wasPlaying = this.store.settings.peel.playing;
    const count = this.volume.count;
    const samplesPerFrame = this.store.settings.look.accumulate
      ? Math.min(this.store.settings.look.maxSamples, 12)
      : 1;

    this.store.set('peel.playing', false);
    this.exporting = true;
    this.recDot.classList.add('show');
    this.showProgress('rendering peel');

    try {
      this.recorder.start({ manual: true, fps: 30 });
      for (let layer = 0; layer < count; layer++) {
        if (!this.exporting) break;
        this.store.set('peel.head', layer, true);
        this.renderer.invalidate();
        for (let sample = 0; sample < samplesPerFrame; sample++) {
          this.drawFrame();
        }
        this.recorder.requestFrame();
        this.setProgress(layer + 1, count, 'rendering peel');
        // One real frame between captures, so the recorder picks up each layer once.
        await nextAnimationFrame();
      }
      const blob = await this.recorder.stop();
      if (blob) {
        const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
        downloadBlob(blob, `timecube-peel-${timestampSlug()}.${extension}`);
        this.toast(`Exported ${count} frames (${(blob.size / 1e6).toFixed(1)} MB).`, 'ok');
      }
    } catch (err) {
      this.toast(err instanceof Error ? err.message : String(err), 'err');
      await this.recorder.stop();
    } finally {
      this.exporting = false;
      this.recDot.classList.remove('show');
      this.hideProgress();
      this.store.set('peel.playing', wasPlaying);
      this.headSlider?.refresh();
    }
  }

  // ----------------------------------------------------------------- dom glue

  private bindDom(): void {
    requireElement<HTMLButtonElement>('empty-open').addEventListener('click', () => this.fileInput.click());
    requireElement<HTMLButtonElement>('empty-demo').addEventListener('click', () => void this.loadSynthetic());
    requireElement<HTMLButtonElement>('empty-webcam').addEventListener('click', () => void this.startWebcam());

    this.fileInput.addEventListener('change', () => {
      const file = this.fileInput.files?.[0];
      this.fileInput.value = '';
      if (file) void this.loadFile(file);
    });

    this.progressCancel.addEventListener('click', () => {
      this.ingest.cancel();
      this.exporting = false;
    });

    let dragDepth = 0;
    window.addEventListener('dragenter', (e) => {
      e.preventDefault();
      dragDepth++;
      this.dropOverlay.classList.add('show');
    });
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) this.dropOverlay.classList.remove('show');
    });
    window.addEventListener('drop', (e) => {
      e.preventDefault();
      dragDepth = 0;
      this.dropOverlay.classList.remove('show');
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      if (file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v|ogv)$/i.test(file.name)) {
        void this.loadFile(file);
      } else if (file.type === 'application/json') {
        void file.text().then((text) => {
          this.store.load(JSON.parse(text) as Partial<Settings>, ['source', 'peel.head']);
          this.panel.refresh();
        });
      } else {
        this.toast('That file is not a video this browser can decode.', 'err');
      }
    });

    const scrub = (clientX: number) => {
      const rect = this.timelineTrack.getBoundingClientRect();
      const fraction = clamp((clientX - rect.left) / Math.max(1, rect.width), 0, 1);
      const maxLayer = Math.max(0, this.volume.count - 1);
      this.store.set('peel.head', fraction * maxLayer);
      this.updateTimeline();
    };

    let scrubbing = false;
    this.timelineTrack.addEventListener('pointerdown', (e) => {
      scrubbing = true;
      this.timelineTrack.setPointerCapture(e.pointerId);
      scrub(e.clientX);
    });
    this.timelineTrack.addEventListener('pointermove', (e) => {
      if (scrubbing) scrub(e.clientX);
    });
    this.timelineTrack.addEventListener('pointerup', (e) => {
      scrubbing = false;
      if (this.timelineTrack.hasPointerCapture(e.pointerId)) {
        this.timelineTrack.releasePointerCapture(e.pointerId);
      }
    });
  }

  private bindKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        return;
      }

      const maxLayer = Math.max(0, this.volume.count - 1);
      const step = e.shiftKey ? 10 : 1;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          this.store.set('peel.playing', !this.store.settings.peel.playing);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.store.set('peel.playing', false);
          this.store.set('peel.head', clamp(Math.round(this.store.settings.peel.head) + step, 0, maxLayer));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.store.set('peel.playing', false);
          this.store.set('peel.head', clamp(Math.round(this.store.settings.peel.head) - step, 0, maxLayer));
          break;
        case 'Home':
          this.store.set('peel.head', 0);
          break;
        case 'End':
          this.store.set('peel.head', maxLayer);
          break;
        case '1':
        case '2':
        case '3':
        case '4':
          this.camera.preset(Number(e.key) - 1);
          break;
        case 'h':
        case 'H':
          document.body.classList.toggle('panel-hidden');
          break;
        case 'f':
        case 'F':
          this.frameCamera();
          break;
        case 'r':
        case 'R':
          void this.toggleRecording();
          break;
        case 's':
        case 'S':
          void this.savePng();
          break;
        case 'o':
        case 'O':
          this.fileInput.click();
          break;
        case '[':
        case ']': {
          const index = PRESETS.findIndex((p) => p.name === this.presetName);
          const delta = e.key === ']' ? 1 : PRESETS.length - 1;
          const next = PRESETS[(index + delta) % PRESETS.length];
          this.presetName = next.name;
          this.applyPreset(next.name);
          break;
        }
        default:
          break;
      }
    });
  }

  // --------------------------------------------------------------------- hud

  private updateTimeline(): void {
    const maxLayer = Math.max(1, this.volume.count - 1);
    const head = this.store.settings.peel.head;
    const fraction = clamp(head / maxLayer, 0, 1);
    this.timelineFill.style.width = `${fraction * 100}%`;
    this.timelineHead.style.left = `${fraction * 100}%`;

    const seconds = this.volume.timestampAt(head);
    this.timelineLayer.textContent =
      this.volume.count > 0
        ? `layer ${Math.round(head)} / ${Math.max(0, this.volume.count - 1)}   ${seconds.toFixed(2)}s`
        : 'no source';
  }

  private updateHud(): void {
    const { width, height, layers } = this.volume;
    const megabytes = this.volume.byteSize / 1e6;
    this.statsEl.textContent = [
      `${this.fps.toFixed(0)} fps`,
      `${width}\u00d7${height}\u00d7${layers}`,
      `${this.lastStats.steps} steps`,
      `${this.lastStats.samples} samples`,
      `${megabytes.toFixed(0)} MB`,
    ].join('   ');
    this.updateTimeline();
  }

  private updateSourceReadout(): void {
    const info = this.ingest.info;
    const { source } = this.store.settings;
    const megabytes = (source.width * source.height * source.layers * 8) / 1e6;
    const lines = [
      info
        ? `<b>${info.name}</b><br>${info.width}\u00d7${info.height} \u00b7 ${info.duration ? `${info.duration.toFixed(1)}s` : 'live'} \u00b7 ~${info.frameRate.toFixed(1)} fps`
        : 'No source loaded.',
      `volume ${source.width}\u00d7${source.height}\u00d7${source.layers} \u00b7 <b>${megabytes.toFixed(0)} MB</b> of GPU memory`,
      this.mode === 'live' ? 'Live ring: the newest frame is always the exposed face.' : '',
    ];
    this.sourceReadout?.set(lines.filter(Boolean).join('<br>'));
  }

  // ------------------------------------------------------------------ overlays

  private showProgress(label: string): void {
    this.progressLabel.textContent = label;
    this.progressFill.style.width = '0%';
    this.progressOverlay.classList.add('show');
  }

  private setProgress(done: number, total: number, label: string): void {
    this.progressLabel.textContent = `${label} \u2014 ${done} / ${total}`;
    this.progressFill.style.width = `${(done / Math.max(1, total)) * 100}%`;
  }

  private hideProgress(): void {
    this.progressOverlay.classList.remove('show');
  }

  private toast(message: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
    const node = document.createElement('div');
    node.className = kind === 'info' ? 'toast' : `toast ${kind}`;
    node.textContent = message;
    this.toasts.appendChild(node);
    window.setTimeout(() => {
      node.style.transition = 'opacity 0.3s ease';
      node.style.opacity = '0';
      window.setTimeout(() => node.remove(), 320);
    }, kind === 'err' ? 7000 : 3600);
  }
}

try {
  new App();
} catch (err) {
  const message = err instanceof Error ? err.message : String(err);
  document.body.innerHTML = `<div style="padding:40px;font:13px/1.7 ui-monospace,monospace;color:#ffbdbd;background:#050507;height:100vh">
    <b style="color:#fff">4D Time Cube could not start</b><br><br>${message}
  </div>`;
  throw err;
}
